import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Loading states shaped like the content they replace.
 *
 * A spinner tells you to wait; a skeleton tells you what is arriving and
 * reserves its space, so the page does not jump when data lands (CLS). Pick
 * the shape that matches the screen: `table` for list screens, `readouts` for
 * KPI rows, `cards` for grids, `detail` for a record page.
 */

const Bar = ({ className }) => (
  <div className={cn('shimmer rounded bg-muted', className)} />
);

const TableSkeleton = ({ rows = 6, columns = 5 }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-card">
    <div className="flex items-center gap-4 border-b border-border bg-muted/40 px-4 py-3">
      {Array.from({ length: columns }).map((_, i) => (
        <Bar key={i} className={cn('h-3', i === 0 ? 'w-32' : 'w-20')} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div
        key={r}
        className="flex items-center gap-4 border-b border-border/60 px-4 py-3.5 last:border-b-0"
      >
        {Array.from({ length: columns }).map((_, c) => (
          <Bar
            key={c}
            className={cn('h-3.5', c === 0 ? 'w-32' : c === columns - 1 ? 'w-16' : 'w-24')}
          />
        ))}
      </div>
    ))}
  </div>
);

const ReadoutsSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="space-y-4 rounded-xl border border-border bg-card p-4"
      >
        <Bar className="h-2.5 w-20" />
        <Bar className="h-7 w-28" />
        <Bar className="h-1.5 w-full" />
      </div>
    ))}
  </div>
);

const CardsSkeleton = ({ count = 6 }) => (
  <div className="go-md2-xl3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="space-y-3 rounded-xl border border-border bg-card p-4"
      >
        <div className="flex items-center justify-between">
          <Bar className="h-4 w-28" />
          <Bar className="h-5 w-16 rounded-full" />
        </div>
        <Bar className="h-3 w-full" />
        <Bar className="h-3 w-3/5" />
      </div>
    ))}
  </div>
);

const DetailSkeleton = () => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Bar className="h-2.5 w-24" />
      <Bar className="h-7 w-64" />
    </div>
    <div className="grid gap-3 lg:grid-cols-3">
      <div className="space-y-3 rounded-xl border border-border bg-card p-4 lg:col-span-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex justify-between gap-6">
            <Bar className="h-3 w-28" />
            <Bar className="h-3 w-40" />
          </div>
        ))}
      </div>
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <Bar className="h-3 w-20" />
        <Bar className="h-20 w-full" />
      </div>
    </div>
  </div>
);

const SHAPES = {
  table: TableSkeleton,
  readouts: ReadoutsSkeleton,
  cards: CardsSkeleton,
  detail: DetailSkeleton,
};

const LoadingState = ({
  shape = 'table',
  label = 'Chargement',
  slowAfterMs = 10000,
  className,
  ...props
}) => {
  const Shape = SHAPES[shape] ?? TableSkeleton;

  // A skeleton with nothing behind it is indistinguishable from a hung
  // request: the screen simply never resolves. After a while, say so — the
  // field app runs on unreliable links, and "the server is slow" is
  // actionable where an eternal placeholder is not.
  const [slow, setSlow] = React.useState(false);
  React.useEffect(() => {
    if (!slowAfterMs) return undefined;
    const id = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(id);
  }, [slowAfterMs]);

  return (
    <div
      className={cn('animate-rise-in space-y-3', className)}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{label}</span>
      <Shape {...props} />
      {slow && (
        <p className="text-center text-xs text-muted-foreground">
          Le serveur met du temps à répondre. Rechargez la page si cela dure.
        </p>
      )}
    </div>
  );
};
LoadingState.displayName = 'LoadingState';

export { LoadingState };
